import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { SERVICES, BOOKING_URL } from "@/data/services";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", dropdown: true },
  { label: "About", to: "/about" },
  { label: "Fees & Funding", to: "/fees-funding" },
  { label: "For Referrers", to: "/for-referrers" },
  { label: "Contact", to: "/contact" },
];

const linkClass = ({ isActive }) =>
  `text-sm font-medium tracking-wide transition-colors duration-300 ${
    isActive ? "text-euca" : "text-charcoal/70 hover:text-euca"
  }`;

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-euca-line bg-euca-mist/90 backdrop-blur-md" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" aria-label="Eucalypt Therapy home" data-testid="header-logo-link">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.label} className="relative group">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium tracking-wide text-charcoal/70 group-hover:text-euca transition-colors duration-300 py-4"
                    data-testid="nav-services-toggle"
                    aria-haspopup="true"
                  >
                    Services
                    <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" />
                  </button>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-1 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-all duration-300">
                    <div className="w-80 rounded-2xl border border-euca-line bg-white shadow-xl shadow-euca-dark/5 p-2" data-testid="nav-services-menu">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`nav-service-${s.slug}`}
                          className="block rounded-xl px-4 py-3 text-sm text-charcoal/80 hover:bg-euca-mist hover:text-euca transition-colors duration-200"
                        >
                          {s.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink key={item.label} to={item.to} className={linkClass} data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {item.label}
                </NavLink>
              )
            )}
            <a
              href="/book"
              data-testid="book-appointment-button"
              className="rounded-full bg-euca px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:bg-euca-dark hover:shadow-md"
            >
              Book Appointment
            </a>
          </nav>

          <button
            type="button"
            className="lg:hidden text-euca-dark p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-euca-line bg-euca-mist" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-1">
            <Link to="/" className="block py-2.5 text-base font-medium text-charcoal" data-testid="mobile-nav-home">Home</Link>
            <p className="pt-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-euca-sage">Services</p>
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="block py-2.5 pl-3 text-base text-charcoal/80" data-testid={`mobile-nav-${s.slug}`}>
                {s.name}
              </Link>
            ))}
            <div className="pt-3 space-y-1">
              <Link to="/about" className="block py-2.5 text-base font-medium text-charcoal" data-testid="mobile-nav-about">About</Link>
              <Link to="/fees-funding" className="block py-2.5 text-base font-medium text-charcoal" data-testid="mobile-nav-fees">Fees &amp; Funding</Link>
              <Link to="/for-referrers" className="block py-2.5 text-base font-medium text-charcoal" data-testid="mobile-nav-referrers">For Referrers</Link>
              <Link to="/contact" className="block py-2.5 text-base font-medium text-charcoal" data-testid="mobile-nav-contact">Contact</Link>
            </div>
            <a
              href="/book"
              data-testid="mobile-book-appointment-button"
              className="mt-4 block rounded-full bg-euca px-6 py-3.5 text-center text-sm font-semibold text-white"
            >
              Book Appointment
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
